import { Field, FieldGroup, FieldLabel, Form } from '../Components/Form';
import * as React from 'react';
import { useMemo } from 'react';
import { useServiceBookingState } from '../context/ServiceBookingState';
import { StepProps } from '../../types';
import { validateVin } from '../Utils/vinValidation';
import { validateLicensePlate } from '../Utils/licensePlateValidation';
import { useTranslation } from 'react-i18next';

// Converts "MAZDA" or "LAND ROVER" to "Mazda" or "Land Rover"
function toTitleCase(str: string): string {
    return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * VIN (Fahrgestellnummer) input field component with validation feedback.
 */
function VinField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
    const { t } = useTranslation();
    const vinValidation = useMemo(() => validateVin(value), [value]);

    return (
        <Field>
            <FieldLabel htmlFor="fgstnr">{t('field.vin.label')}</FieldLabel>
            <input
                name="fgstnr"
                value={value}
                onChange={(e) => onChange(e.target.value.toUpperCase())}
                required
                minLength={3}
                type="text"
                maxLength={25}
                placeholder={t('field.vin.placeholder')}
                id="fgstnr"
            />
            {vinValidation.message && (
                <p
                    className={`mt-1 text-sm ${
                        vinValidation.messageType === 'success'
                            ? 'text-green-600'
                            : vinValidation.messageType === 'error'
                            ? 'text-red-600'
                            : 'text-orange-600'
                    }`}
                >
                    {vinValidation.message}
                </p>
            )}
        </Field>
    );
}

/**
 * License plate input field component with validation feedback.
 * Validates Austrian and European license plate formats.
 */
function LicensePlateField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
    const { t } = useTranslation();
    const validation = useMemo(() => validateLicensePlate(value), [value]);

    return (
        <Field>
            <FieldLabel htmlFor="kennzeichen">{t('field.licensePlate.label')}</FieldLabel>
            <input
                name="kennzeichen"
                value={value}
                onChange={(e) => onChange(e.target.value.toUpperCase())}
                type="text"
                maxLength={20}
                placeholder={t('field.licensePlate.placeholder')}
                required
                minLength={3}
                id="kennzeichen"
            />
            {validation.message && (
                <p
                    className={`mt-1 text-sm ${
                        validation.messageType === 'success'
                            ? 'text-green-600'
                            : validation.messageType === 'error'
                            ? 'text-red-600'
                            : 'text-orange-600'
                    }`}
                >
                    {validation.message}
                </p>
            )}
        </Field>
    );
}

export default function CarInfo({ form, setForm, onStepCompleted }: StepProps) {
    const { t } = useTranslation();
    const { makes } = useServiceBookingState();

    const allMakes = Array.from(
        new Map(makes.map((m) => [m.fabrikat_code, { value: m.fabrikat_code, label: toTitleCase(m.marke) }])).values()
    ).sort((a, b) => a.label.localeCompare(b.label));

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        // Validate VIN before submission
        const vinValidation = validateVin(form.fahrzeug.fgstnr);
        if (!vinValidation.isValid) {
            return;
        }
        onStepCompleted();
    };

    return (
        <Form className="mx-auto max-w-md" onSubmit={handleSubmit}>
            <FieldGroup>
                <Field>
                    <FieldLabel htmlFor="fabrikat_code">{t('field.make.label')}</FieldLabel>
                    <select
                        name="fabrikat_code"
                        value={form.fahrzeug.fabrikat_code}
                        onChange={(e) =>
                            setForm((f: any) => ({
                                ...f,
                                fahrzeug: {
                                    ...(f?.fahrzeug ?? {}),
                                    fabrikat_code: e.target.value,
                                },
                            }))
                        }
                        required
                        id="fabrikat_code"
                    >
                        <option value="">{t('action.select')}</option>
                        {allMakes.map((make) => (
                            <option key={make.value} value={make.value}>
                                {make.label}
                            </option>
                        ))}
                    </select>
                </Field>

                <Field>
                    <FieldLabel htmlFor="modell">{t('field.model.label')}</FieldLabel>
                    <input
                        name="modell"
                        value={form.fahrzeug.modell}
                        onChange={(e) =>
                            setForm((f: any) => ({
                                ...f,
                                fahrzeug: {
                                    ...(f?.fahrzeug ?? {}),
                                    modell: e.target.value,
                                },
                            }))
                        }
                        type="text"
                        maxLength={50}
                        placeholder={t('field.model.placeholder')}
                        required
                        id="modell"
                    />
                </Field>

                <LicensePlateField
                    value={form.fahrzeug.kennzeichen}
                    onChange={(value) =>
                        setForm((f: any) => ({
                            ...f,
                            fahrzeug: {
                                ...(f?.fahrzeug ?? {}),
                                kennzeichen: value,
                            },
                        }))
                    }
                />

                <VinField
                    value={form.fahrzeug.fgstnr}
                    onChange={(value) =>
                        setForm((f: any) => ({
                            ...f,
                            fahrzeug: {
                                ...(f?.fahrzeug ?? {}),
                                fgstnr: value,
                            },
                        }))
                    }
                />

                <Field>
                    <FieldLabel htmlFor="km">{t('field.mileage.label')}</FieldLabel>
                    <input
                        name="km"
                        value={form.fahrzeug.km ?? undefined}
                        onChange={(e) =>
                            setForm((f: any) => ({
                                ...f,
                                fahrzeug: {
                                    ...(f?.fahrzeug ?? {}),
                                    km: e.target.value,
                                },
                            }))
                        }
                        type="number"
                        min="0"
                        placeholder={t('field.mileage.placeholder')}
                        required
                        id="km"
                    />
                </Field>

                <Field>
                    <FieldLabel htmlFor="erstzulassung">{t('field.firstRegistration.label')}</FieldLabel>
                    <input
                        name="erstzulassung"
                        value={form.fahrzeug.erstzulassung}
                        onChange={(e) =>
                            setForm((f: any) => ({
                                ...f,
                                fahrzeug: {
                                    ...(f?.fahrzeug ?? {}),
                                    erstzulassung: e.target.value,
                                },
                            }))
                        }
                        type="date"
                        min={1900}
                        max={9999}
                        placeholder={t('field.firstRegistration.placeholder')}
                        required
                        id="erstzulassung"
                    />
                </Field>
            </FieldGroup>

            <div className={'mt-7 flex'}>
                <button type="submit" className="disabled:opacity-50">
                    {t('action.continue')}
                </button>
            </div>
        </Form>
    );
}
